import React from "react";
import VariantGate from './_variant_gate';
import HistoryPageV2 from '../components/history_page_v2/history_page_v2';
import HistoryPageV3 from '../components/history_page_v3/history_page_v3';
import dayjs from "dayjs";
import { showToast } from "../utils/show_toast";
import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import FilterPill from "../components/common/filter_pill";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import TotalBalanceChart from "../components/history_page/total_balance_chart";
import { fetchBalanceHistory, fetchMonthlySummary } from "../services/balanceApi";
import { getSeries as getStockSeries } from "../services/stockApi";
import { getSeries as getBtcSeries } from "../services/btcApi";
import { getStoredUsername, requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";

class HistoryPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: false,
            lastUpdated: null,
            balanceHistory: [],
            historyMode: "30d",
            selectedMonth: dayjs().format("YYYY-MM"),
            availableMonths: [],
            stockSeries: [],
            btcSeries: [],
        };
        this.isPolling = false;
        this.shouldPoll = true;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        this.pollData();
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        this.isPolling = true;
        const username = getStoredUsername();
        if (!username) { this.isPolling = false; return; }
        this.setState({ loading: true });
        const { historyMode, selectedMonth } = this.state;

        // 미국주식 일별 평가액 시리즈 — 별도 조회, 실패해도 기존 히스토리 기능에 영향 없음
        getStockSeries("all")
            .then((res) => {
                const points = Array.isArray(res?.data?.points) ? res.data.points : [];
                this.setState({ stockSeries: points });
            })
            .catch((err) => {
                console.error("미국주식 시리즈 로드 실패", err);
            });

        // BTC 적립 일별 스냅샷 — 총잔고 차트에서 코인 밴드를 델타/BTC 로 가르는 데 쓴다 (DAS-181).
        // 실패해도 밴드가 갈라지지 않을 뿐 차트는 종전대로 그려진다.
        getBtcSeries("all")
            .then((res) => {
                const points = Array.isArray(res?.data?.points) ? res.data.points : [];
                this.setState({ btcSeries: points });
            })
            .catch((err) => {
                console.error("BTC 적립 시리즈 로드 실패", err);
            });

        const summaryPromise = fetchMonthlySummary(username)
            .then((res) => (Array.isArray(res.data) ? res.data : []))
            .catch((err) => { console.error("월별 요약 로드 실패", err); showToast({ type: "error", message: "월별 요약 로드에 실패했습니다." }); return []; });

        summaryPromise
            .then((summary) => {
                const monthsSet = new Set();
                summary.forEach((item) => {
                    (item?.summary || []).forEach((row) => { if (row?.month) monthsSet.add(row.month); });
                });
                const availableMonths = Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
                let resolvedMonth = selectedMonth;
                if (availableMonths.length > 0 && !availableMonths.includes(resolvedMonth))
                    resolvedMonth = availableMonths[0];
                if (!resolvedMonth) resolvedMonth = availableMonths.length > 0 ? availableMonths[0] : "";
                const params = historyMode === "month"
                    ? (resolvedMonth ? { month: resolvedMonth } : {})
                    : { start_date: dayjs().subtract(30, 'day').format("YYYY-MM-DD") };

                return fetchBalanceHistory(username, params)
                    .then((historyResponse) => {
                        const history = Array.isArray(historyResponse.data) ? historyResponse.data : [];
                        this.setState({ balanceHistory: history, availableMonths, selectedMonth: resolvedMonth, lastUpdated: new Date() });
                    })
                    .catch((err) => {
                        console.error("히스토리 로드 실패", err);
                        showToast({ type: "error", message: "히스토리 로드에 실패했습니다." });
                        this.setState({ availableMonths, selectedMonth: resolvedMonth, lastUpdated: new Date() });
                    });
            })
            .finally(() => {
                this.isPolling = false;
                this.setState({ loading: false });
                if (this.shouldPoll) this.pollTimer = setTimeout(this.pollData, 5 * 60 * 1000);
            });
    };

    setHistoryMode = (mode) => { this.setState({ historyMode: mode }, () => { this.pollData(); }); };
    handleMonthChange = (event) => { this.setState({ selectedMonth: event.target.value }); };
    handleMonthFetch = () => {
        if (!this.state.selectedMonth) return;
        this.setState({ historyMode: "month" }, () => { this.pollData(); });
    };
    onCollapse = (collapsed) => { setNavCollapsed(collapsed); this.setState({ collapsed }); };
    handleRefresh = () => { this.pollData(); };

    formatLastUpdated = (date) => {
        if (!date) return "";
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
    };

    render() {
        const {
            collapsed, loading, lastUpdated, balanceHistory,
            historyMode, selectedMonth, availableMonths, stockSeries, btcSeries,
        } = this.state;
        const isMonthMode = historyMode === "month";

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? "sm:pl-[56px]" : "sm:pl-[220px]"}`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:mx-6 sm:my-5 sm:p-6 lg:min-w-[960px]">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm text-content-secondary">잔고 / 히스토리</div>
                            <div className="flex items-center gap-2">
                                {lastUpdated && (
                                    <span className="font-num text-xs text-content-muted">
                                        {this.formatLastUpdated(lastUpdated)} 갱신
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={this.handleRefresh}
                                    disabled={loading}
                                    aria-label="데이터 새로고침"
                                    className="flex min-h-[44px] items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
                                >
                                    {loading ? (
                                        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : (
                                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                            <path d="M21 3v5h-5" />
                                        </svg>
                                    )}
                                    <span className="hidden sm:inline">{loading ? "갱신 중..." : "새로고침"}</span>
                                </button>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-content-secondary">
                            <span className="text-content-muted">조회</span>
                            {/* 최근 30일 버튼 → FilterPill solid */}
                            <FilterPill
                                active={historyMode === "30d"}
                                onClick={() => this.setHistoryMode("30d")}
                                variant="solid"
                            >
                                최근 30일
                            </FilterPill>
                            <div className="flex flex-wrap items-center gap-2">
                                <select
                                    value={selectedMonth}
                                    onChange={this.handleMonthChange}
                                    className="rounded-md border border-surface-border bg-surface-elevated px-2 py-1 text-xs text-content-secondary focus:border-brand focus:outline-none"
                                    disabled={availableMonths.length === 0}
                                    aria-label="조회 월 선택"
                                >
                                    {availableMonths.length === 0 && <option value="">데이터 없음</option>}
                                    {availableMonths.map((month) => (
                                        <option key={month} value={month}>{month}</option>
                                    ))}
                                </select>
                                {/* 월 전체 조회 버튼 → FilterPill solid */}
                                <FilterPill
                                    active={isMonthMode}
                                    onClick={this.handleMonthFetch}
                                    variant="solid"
                                    className={availableMonths.length === 0 ? "cursor-not-allowed opacity-50" : ""}
                                >
                                    월 전체 조회
                                </FilterPill>
                            </div>
                        </div>

                        <div className="mt-4">
                            <TotalBalanceChart balanceHistory={balanceHistory} stockSeries={stockSeries} btcSeries={btcSeries} />
                        </div>

                        {/* 월별 손익 요약과 상세 내역(전략 탭)은 대시보드 델타전략 뷰로 옮겼다 (DAS-179).
                            전략 단위로 읽는 표라 뷰가 곧 전략인 곳에서 보는 편이 낫고, 그곳에서는
                            BTC 적립분을 뺀 값으로 계산된다. 총잔고 히스토리는 자산 전체를 보는 자리라 여기 남는다. */}
                        <div className="mt-6 rounded-2xl border border-surface-border bg-surface-card-alt p-4">
                            <div className="text-sm font-semibold text-content-secondary">월별 손익 요약 · 상세 내역이 옮겨졌습니다</div>
                            <p className="mt-2 text-xs leading-relaxed text-content-muted">
                                전략 단위로 읽는 두 표는 대시보드의 <span className="text-content-secondary">델타전략</span> 뷰에서 볼 수 있습니다.
                                그곳에서는 BTC 적립분을 뺀 값으로 계산됩니다.
                            </p>
                            <a
                                href="/dashboard"
                                className="mt-3 inline-flex min-h-[44px] items-center rounded-lg border border-brand/30 bg-brand/10 px-4 text-xs font-medium text-brand-light transition-colors hover:bg-brand/20"
                            >
                                델타전략 뷰 열기
                            </a>
                        </div>
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

// DAS-70 Wave-A: variant 슬롯 3-단 확장 (v1 / v2 슬롯 / v3 정보 구조 재설계)
const HistoryPageV1 = HistoryPage;
export { HistoryPageV1 };
const HistoryPageWithVariant = (props) => (
    <VariantGate v1={HistoryPageV1} v2={HistoryPageV2} v3={HistoryPageV3} fallbackToV1={true} {...props} />
);
export default HistoryPageWithVariant;
